import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const ROLE_BADGE = {
  admin:    { bg: '#eff4ff', color: '#1a56db' },
  operator: { bg: '#fff7ed', color: '#c2410c' },
  viewer:   { bg: '#f0fdf4', color: '#16a34a' },
};

function fmtDate(d) {
  if (!d) return 'Never';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function UserManagement() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg,     setMsg]     = useState({ text: '', type: '' });

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [editUser,   setEditUser]   = useState(null);   // user being edited
  const [form,       setForm]       = useState({ username: '', password: '', role: 'viewer' });
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getUsers();
      setUsers(res.data);
    } catch (e) {
      flash('Failed to load users: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  }

  function openCreate() {
    setForm({ username: '', password: '', role: 'viewer' });
    setEditUser(null);
    setShowCreate(true);
  }

  function openEdit(user) {
    setForm({ username: user.username, password: '', role: user.role });
    setEditUser(user);
    setShowCreate(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editUser) {
        // Update role and optionally password
        const update = { role: form.role };
        if (form.password) update.password = form.password;
        await api.updateUser(editUser.id, update);
        flash(`User "${editUser.username}" updated.`);
      } else {
        await api.createUser({ username: form.username, password: form.password, role: form.role });
        flash(`User "${form.username}" created.`);
      }
      setShowCreate(false);
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(user) {
    try {
      await api.updateUser(user.id, { active: !user.active });
      flash(`User "${user.username}" ${user.active ? 'deactivated' : 'reactivated'}.`);
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">User Management</div>
          <div className="page-subtitle">Manage dashboard users and role-based access</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ New User</button>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.type === 'error' ? '⚠' : '✔'} {msg.text}
        </div>
      )}

      {/* ── User table ── */}
      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">👤</div>
            Users ({users.length})
          </div>
          <button className="refresh-btn" onClick={load} disabled={loading}>↻ Refresh</button>
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={6} className="empty">No users found.</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1a56db, #0ea5e9)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0
                      }}>
                        {u.username.slice(0, 2).toUpperCase()}
                      </div>
                      <strong>{u.username}</strong>
                    </div>
                  </td>
                  <td>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 10px',
                      borderRadius: 12, textTransform: 'uppercase',
                      background: ROLE_BADGE[u.role]?.bg || '#f1f5f9',
                      color: ROLE_BADGE[u.role]?.color || '#64748b'
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.active ? 'online' : 'offline'}`}>
                      {u.active ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(u.lastLoginAt)}</td>
                  <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(u.createdAt)}</td>
                  <td>
                    <div className="flex-gap">
                      <button className="btn btn-sm btn-ghost" onClick={() => openEdit(u)}>
                        ✏ Edit
                      </button>
                      <button
                        className={`btn btn-sm ${u.active ? 'btn-danger' : 'btn-outline'}`}
                        onClick={() => toggleActive(u)}
                      >
                        {u.active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Role reference ── */}
      <div className="panel" style={{ padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#1e2a3b' }}>
          Role Permissions
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            {
              role: 'admin', color: '#1a56db', bg: '#eff4ff',
              perms: ['Full access to all pages', 'Deploy and enforce patches', 'Rollback patches',
                      'Manage users and roles', 'Run compliance evaluation', 'Delete devices',
                      'Approve/reject all requests']
            },
            {
              role: 'operator', color: '#c2410c', bg: '#fff7ed',
              perms: ['Submit approval requests', 'View own request status', 'View all dashboards',
                      'Cannot deploy patches directly', 'Cannot manage users',
                      'Cannot approve requests']
            },
            {
              role: 'viewer', color: '#16a34a', bg: '#f0fdf4',
              perms: ['View all dashboards and reports', 'View compliance status', 'View patch history',
                      'View device list', 'Cannot deploy or enforce patches', 'Cannot manage users']
            }
          ].map(r => (
            <div key={r.role} style={{
              border: `1px solid ${r.bg}`, borderRadius: 8, padding: 16,
              background: r.bg
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: r.color,
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                {r.role}
              </div>
              {r.perms.map((p, i) => (
                <div key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 4,
                  display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: p.startsWith('Cannot') ? '#dc2626' : '#16a34a' }}>
                    {p.startsWith('Cannot') ? '✘' : '✔'}
                  </span>
                  {p}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Create / Edit Modal ── */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 32, width: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 20 }}>
              {editUser ? `Edit User — ${editUser.username}` : 'Create New User'}
            </div>

            <form onSubmit={handleSave}>
              {!editUser && (
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    className="form-input"
                    type="text"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    required
                    autoFocus
                    placeholder="e.g. john.smith"
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  {editUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  className="form-input"
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required={!editUser}
                  placeholder={editUser ? 'Leave blank to keep current' : 'Min 8 characters'}
                  minLength={form.password ? 8 : undefined}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                >
                  <option value="admin">Admin — full access</option>
                  <option value="operator">Operator — submit approval requests</option>
                  <option value="viewer">Viewer — read only</option>
                </select>
              </div>

              <div className="flex-gap" style={{ marginTop: 24, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '⟳ Saving...' : editUser ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
